import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WAITLIST_FLAG_KEY } from "../lib/constants";
import { track } from "../lib/analytics";
import { isEmail, isPhone, required } from "../lib/validators";

const ROLE_OPTIONS = [
  "Founder/CEO",
  "Operations",
  "Sales/Marketing",
  "Finance",
  "Other",
];
const CATEGORY_OPTIONS = [
  "Food & Beverages",
  "Personal Care/Beauty",
  "Home Care",
  "Fashion/Accessories",
  "Other",
];
const YEARS_OPTIONS = ["0–1", "2–3", "4–6", "7+"];
const CAPACITY_OPTIONS = ["<100 units", "100–500", "500–2,000", "2,000+"];
const COMPLIANCE_OPTIONS = ["Yes (Fully compliant)", "In progress", "Not yet"];
const SALES_CHANNELS = [
  "Instagram",
  "WhatsApp",
  "Retail stores",
  "Distributors/Wholesale",
  "E-commerce platforms",
  "Other",
];
const GOAL_OPTIONS = [
  "Access global buyers",
  "Increase local distribution",
  "Become export-ready",
  "Access working capital",
  "Improve operations/structure",
  "Other",
];
const FUNDING_OPTIONS = [
  "Inventory credit",
  "Working capital loan",
  "Investor capital",
  "Not now",
];
const TIMELINE_OPTIONS = [
  "Immediately (0–3 months)",
  "3–6 months",
  "6–12 months",
  "12+ months",
];

function getInitialForm() {
  return {
    full_name: "",
    email: "",
    phone: "",
    role: "Founder/CEO",
    location_city: "",
    location_state: "",

    business_name: "",
    brand_category: "Food & Beverages",
    years_in_business: "0–1",
    monthly_production_capacity: "<100 units",
    has_registered_business: false,
    compliance_readiness: "In progress",
    current_sales_channels: ["Instagram"],

    primary_goal: "Access global buyers",
    funding_interest: "Inventory credit",
    expected_timeline: "3–6 months",

    consent_marketing: false,

    hp_field: "",
  };
}

export default function WaitlistModal({
  isOpen,
  onClose,
  sourceCta,
  toastApi,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(getInitialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);
  const lastActiveElementRef = useRef(null);

  const functionUrl = useMemo(() => {
    const base = process.env.REACT_APP_SUPABASE_URL;
    return base ? `${base}/functions/v1/waitlist-submit` : "";
  }, []);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleSalesChannel(channel) {
    setForm((prev) => {
      const exists = prev.current_sales_channels.includes(channel);
      const next = exists
        ? prev.current_sales_channels.filter((c) => c !== channel)
        : [...prev.current_sales_channels, channel];

      return {
        ...prev,
        current_sales_channels: next.length ? next : [channel],
      };
    });
  }

  function validate(nextForm) {
    const e = {};

    if (!required(nextForm.full_name)) e.full_name = "Full name is required.";
    if (!required(nextForm.email)) e.email = "Email is required.";
    else if (!isEmail(nextForm.email)) e.email = "Enter a valid email.";

    if (!required(nextForm.phone)) e.phone = "Phone is required.";
    else if (!isPhone(nextForm.phone)) e.phone = "Enter a valid phone number.";

    if (!required(nextForm.location_city))
      e.location_city = "City is required.";
    if (!required(nextForm.location_state))
      e.location_state = "State is required.";

    if (!required(nextForm.business_name))
      e.business_name = "Business name is required.";

    if (
      !Array.isArray(nextForm.current_sales_channels) ||
      nextForm.current_sales_channels.length < 1
    ) {
      e.current_sales_channels = "Select at least one channel.";
    }

    if (nextForm.consent_marketing !== true) {
      e.consent_marketing = "Consent is required.";
    }

    return e;
  }

  async function submit() {
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    track("waitlist_submit_attempt", { source_cta: sourceCta });

    setSubmitting(true);
    try {
      if (!functionUrl) {
        toastApi.show("error", "Missing Supabase URL. Check your .env file.");
        setSubmitting(false);
        return;
      }

      const res = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || "",
          Authorization: `Bearer ${
            process.env.REACT_APP_SUPABASE_ANON_KEY || ""
          }`,
        },
        body: JSON.stringify({
          ...form,
          email: form.email.trim().toLowerCase(),
          source_cta: sourceCta || "unknown",
        }),
      });

      let body = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }

      if (!res.ok) {
        const msg =
          (body && (body.error || body.message)) ||
          (res.status === 429
            ? "Too many attempts. Try again later."
            : "Submission failed. Try again.");
        toastApi.show("error", msg);
        setSubmitting(false);
        return;
      }

      // Honeypot bots: server returns ok: true, skipped: true. Treat as success but do not set flag.
      if (body && body.skipped) {
        onClose();
        navigate("/success");
        return;
      }

      if (body && body.already_exists) {
        toastApi.show(
          "info",
          "You’re already on the waitlist. We’ll keep you updated."
        );
      } else {
        toastApi.show("success", "Application received. Redirecting...");
      }

      localStorage.setItem(WAITLIST_FLAG_KEY, "true");
      track("waitlist_submit_success", {
        source_cta: sourceCta,
        already_exists: !!(body && body.already_exists),
      });

      onClose();
      navigate("/success");
    } catch (err) {
      toastApi.show(
        "error",
        "Network error. Check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Open/close behavior + focus management
  useEffect(() => {
    if (!isOpen) return;

    lastActiveElementRef.current = document.activeElement;
    setErrors({});
    setSubmitting(false);

    window.setTimeout(() => {
      if (firstFocusableRef.current) firstFocusableRef.current.focus();
    }, 0);

    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key !== "Tab") return;

      const focusable = modalRef.current
        ? modalRef.current.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          )
        : [];

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
      const el = lastActiveElementRef.current;
      if (el && el.focus) el.focus();
    };
  }, [isOpen, onClose]);

  // Reset form when closing
  useEffect(() => {
    if (isOpen) return;
    setForm(getInitialForm());
    setErrors({});
    setSubmitting(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="modalOverlay"
      ref={overlayRef}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        className="modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="SmartUp360 waitlist application form"
      >
        <div className="modal__header">
          <div>
            <div className="modal__title">Join SmartUp360</div>
            <div className="modal__subtitle">
              Apply for the showcase or join the 2026 waitlist.
            </div>
          </div>
          <button className="iconBtn" onClick={onClose} aria-label="Close form">
            ×
          </button>
        </div>

        <div className="modal__body">
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!submitting) submit();
            }}
          >
            <div className="form__sectionTitle">Contact and identity</div>

            <div className="formGrid">
              <div className="field">
                <label htmlFor="full_name">Full name</label>
                <input
                  ref={firstFocusableRef}
                  id="full_name"
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setField("full_name", e.target.value)}
                  placeholder="Your full name"
                  autoComplete="name"
                />
                {errors.full_name ? (
                  <div className="field__error">{errors.full_name}</div>
                ) : null}
              </div>

              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="you@business.com"
                  autoComplete="email"
                />
                {errors.email ? (
                  <div className="field__error">{errors.email}</div>
                ) : null}
              </div>

              <div className="field">
                <label htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="+234..."
                  autoComplete="tel"
                />
                {errors.phone ? (
                  <div className="field__error">{errors.phone}</div>
                ) : null}
              </div>

              <div className="field">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={form.role}
                  onChange={(e) => setField("role", e.target.value)}
                >
                  {ROLE_OPTIONS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="location_city">City</label>
                <input
                  id="location_city"
                  type="text"
                  value={form.location_city}
                  onChange={(e) => setField("location_city", e.target.value)}
                  placeholder="Lagos"
                />
                {errors.location_city ? (
                  <div className="field__error">{errors.location_city}</div>
                ) : null}
              </div>

              <div className="field">
                <label htmlFor="location_state">State</label>
                <input
                  id="location_state"
                  type="text"
                  value={form.location_state}
                  onChange={(e) => setField("location_state", e.target.value)}
                  placeholder="Lagos State"
                />
                {errors.location_state ? (
                  <div className="field__error">{errors.location_state}</div>
                ) : null}
              </div>
            </div>

            <div className="form__divider" />

            <div className="form__sectionTitle">Business profile</div>

            <div className="formGrid">
              <div className="field">
                <label htmlFor="business_name">Business name</label>
                <input
                  id="business_name"
                  type="text"
                  value={form.business_name}
                  onChange={(e) => setField("business_name", e.target.value)}
                  placeholder="Brand or company name"
                />
                {errors.business_name ? (
                  <div className="field__error">{errors.business_name}</div>
                ) : null}
              </div>

              <div className="field">
                <label htmlFor="brand_category">Brand category</label>
                <select
                  id="brand_category"
                  value={form.brand_category}
                  onChange={(e) => setField("brand_category", e.target.value)}
                >
                  {CATEGORY_OPTIONS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="years_in_business">Years in business</label>
                <select
                  id="years_in_business"
                  value={form.years_in_business}
                  onChange={(e) =>
                    setField("years_in_business", e.target.value)
                  }
                >
                  {YEARS_OPTIONS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="monthly_production_capacity">
                  Monthly production capacity
                </label>
                <select
                  id="monthly_production_capacity"
                  value={form.monthly_production_capacity}
                  onChange={(e) =>
                    setField("monthly_production_capacity", e.target.value)
                  }
                >
                  {CAPACITY_OPTIONS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field field--row">
                <label htmlFor="has_registered_business">
                  Registered business?
                </label>
                <div className="toggleRow">
                  <button
                    type="button"
                    className={`toggleBtn ${
                      form.has_registered_business ? "toggleBtn--on" : ""
                    }`}
                    onClick={() => setField("has_registered_business", true)}
                    aria-pressed={form.has_registered_business === true}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`toggleBtn ${
                      form.has_registered_business ? "" : "toggleBtn--on"
                    }`}
                    onClick={() => setField("has_registered_business", false)}
                    aria-pressed={form.has_registered_business === false}
                  >
                    No
                  </button>
                </div>
              </div>

              <div className="field">
                <label htmlFor="compliance_readiness">
                  Compliance readiness
                </label>
                <select
                  id="compliance_readiness"
                  value={form.compliance_readiness}
                  onChange={(e) =>
                    setField("compliance_readiness", e.target.value)
                  }
                >
                  {COMPLIANCE_OPTIONS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label>Current sales channels</label>
              <div className="chips">
                {SALES_CHANNELS.map((c) => {
                  const active = form.current_sales_channels.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      className={`chip ${active ? "chip--active" : ""}`}
                      onClick={() => toggleSalesChannel(c)}
                      aria-pressed={active}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
              {errors.current_sales_channels ? (
                <div className="field__error">
                  {errors.current_sales_channels}
                </div>
              ) : null}
            </div>

            <div className="form__divider" />

            <div className="form__sectionTitle">Growth intent</div>

            <div className="formGrid">
              <div className="field">
                <label htmlFor="primary_goal">Primary goal</label>
                <select
                  id="primary_goal"
                  value={form.primary_goal}
                  onChange={(e) => setField("primary_goal", e.target.value)}
                >
                  {GOAL_OPTIONS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="funding_interest">Funding interest</label>
                <select
                  id="funding_interest"
                  value={form.funding_interest}
                  onChange={(e) => setField("funding_interest", e.target.value)}
                >
                  {FUNDING_OPTIONS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="expected_timeline">Expected timeline</label>
                <select
                  id="expected_timeline"
                  value={form.expected_timeline}
                  onChange={(e) =>
                    setField("expected_timeline", e.target.value)
                  }
                >
                  {TIMELINE_OPTIONS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form__divider" />

            <div className="field">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={form.consent_marketing}
                  onChange={(e) =>
                    setField("consent_marketing", e.target.checked)
                  }
                />
                <span>
                  I agree to be contacted by SmartUp360 about the showcase,
                  waitlist and related opportunities.
                </span>
              </label>
              {errors.consent_marketing ? (
                <div className="field__error">{errors.consent_marketing}</div>
              ) : null}
            </div>

            <input
              type="text"
              value={form.hp_field}
              onChange={(e) => setField("hp_field", e.target.value)}
              name="hp_field"
              className="hpField"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <div className="form__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit application"}
              </button>
            </div>

            <div className="form__fineprint">
              We use your responses to assess export readiness and platform fit.
            </div>

            <span
              ref={lastFocusableRef}
              tabIndex={0}
              style={{ position: "absolute", left: "-9999px" }}
            />
          </form>
        </div>
      </div>
    </div>
  );
}
